// @docs:
//   - docs/core/operations/ROUTING_SERVICE.md
//
// The boat's polar as a picture: one curve per wind speed, 0° (head to wind) at the top
// to 180° (dead downwind) at the bottom, boat speed outwards. It draws the completed
// table the API returns, so every curve runs the full 0–180°.

import type { RoutingPolar } from '../../services/api';

const W = 240, H = 300, CX = 24, CY = 150, R = 136;

/** Light to deep blue, light air to strong wind. */
const colour = (j: number, n: number) => `hsl(${195 + (n <= 1 ? 0 : (j / (n - 1)) * 40)} 85% ${72 - (n <= 1 ? 0 : (j / (n - 1)) * 30)}%)`;

export function PolarPicture({ polar }: { polar: RoutingPolar }) {
  const max = Math.max(1, ...polar.stw.flat());
  const ring = max > 10 ? 4 : 2;
  const xy = (twa: number, stw: number) => {
    const r = (stw / max) * R, a = (twa * Math.PI) / 180;
    return [(CX + r * Math.sin(a)).toFixed(1), (CY - r * Math.cos(a)).toFixed(1)];
  };
  const point = (twa: number, stw: number) => xy(twa, stw).join(',');
  const n = polar.twsKt.length;
  return (
    <div className="flex items-start gap-3 flex-wrap">
      <svg role="img" aria-label="Polar diagram" viewBox={`0 0 ${W} ${H}`} className="w-[240px] max-w-full h-auto bg-slate-900/60 rounded-lg">
        {Array.from({ length: Math.floor(max / ring) }, (_, k) => (k + 1) * ring).map((s) => {
          const r = (s / max) * R;
          return <path key={s} d={`M ${CX} ${CY - r} A ${r} ${r} 0 0 1 ${CX} ${CY + r}`} fill="none" stroke="rgb(71 85 105)" strokeWidth="0.6" />;
        })}
        {[0, 45, 90, 135, 180].map((a) => {
          const [x, y] = xy(a, max);
          return <line key={a} x1={CX} y1={CY} x2={x} y2={y} stroke="rgb(71 85 105)" strokeWidth="0.6" />;
        })}
        {polar.twsKt.map((tws, j) => (
          <path
            key={tws}
            data-tws={tws}
            d={`M ${polar.twaDeg.map((a, i) => point(a, polar.stw[i][j])).join(' L ')}`}
            fill="none"
            stroke={colour(j, n)}
            strokeWidth="1.6"
          />
        ))}
      </svg>
      <ul className="text-[13px] text-slate-300 space-y-0.5" aria-label="Wind speeds">
        {polar.twsKt.map((tws, j) => (
          <li key={tws} className="flex items-center gap-2">
            <span className="inline-block w-4 h-[3px] rounded" style={{ background: colour(j, n) }} />
            {tws} kn
          </li>
        ))}
        <li className="pt-1 text-slate-400">Rings every {ring} kn</li>
      </ul>
    </div>
  );
}
